import { useCallback, useMemo } from 'react';
import { MapLocationData } from '@hero/web-components';

export interface UseMapBoundsOptions {
  /** Padding in pixels when fitting bounds */
  padding?: number;
  /** Maximum zoom level when fitting bounds */
  maxZoom?: number;
  /** Minimum zoom level when fitting bounds */
  minZoom?: number;
}

/**
 * Hook for managing map bounds
 * Handles auto-fitting bounds for markers and routes
 */
export function useMapBounds(options: UseMapBoundsOptions = {}) {
  const { padding = 50, maxZoom = 14, minZoom = 2 } = options;

  /**
   * Fit bounds to show all locations
   */
  const fitBounds = useCallback(
    (map: google.maps.Map | null, locations: MapLocationData[]) => {
      if (!map || locations.length === 0) return;

      const bounds = new google.maps.LatLngBounds();

      // Add all location coordinates to bounds
      locations.forEach(location => {
        bounds.extend(location.cords);
      });

      map.fitBounds(bounds, {
        top: padding + 50,
        right: padding + 60,
        bottom: padding + 30,
        left: padding + 260,
      });

      // Apply zoom constraints (use idle event instead of bounds_changed to prevent lag)
      const listener = google.maps.event.addListenerOnce(map, 'idle', () => {
        const currentZoom = map.getZoom();
        if (currentZoom !== undefined) {
          if (currentZoom > maxZoom) {
            map.setZoom(maxZoom);
          } else if (currentZoom < minZoom) {
            map.setZoom(minZoom);
          }
        }
      });
    },
    [padding, maxZoom, minZoom],
  );

  return {
    fitBounds,
  };
}
